import type { DemoFormData, IletisimKanali } from "./demoTypes";
import type {
  DemoFlow,
  DemoFlowStep,
  DemoFlowQuestionStep,
  DemoFlowCtaStep,
  DemoFlowActionSetAction,
} from "./flowTypes";
import type { SectorConfig } from "./sectorConfigs";
import { findSectorConfig, getItemLabel, resolveCtaIntent } from "./sectorConfigs";

export const SORU_SENTENCE_MAP: Record<string, string> = {
  "Adı": "Adınızı öğrenebilir miyiz?",
  "Telefonu": "Size ulaşabileceğimiz telefon numaranızı paylaşır mısınız?",
  "İlgilendiği hizmet": "Hangi hizmetimizle ilgileniyorsunuz?",
  "Bulunduğu bölge": "Hangi bölgeden iletişime geçiyorsunuz?",
  "Randevu tarihi": "Hangi tarih sizin için uygun?",
  "Bütçe": "Planladığınız yaklaşık bütçe nedir?",
  "Etkinlik tarihi": "Etkinliğinizi hangi tarihte planlıyorsunuz?",
  "Kişi sayısı": "Yaklaşık kaç kişilik bir organizasyon düşünüyorsunuz?",
  "Araç marka/modeli": "Araç marka ve modelinizi paylaşır mısınız?",
  "Özel not": "Eklemek istediğiniz başka bir detay var mı?",
};

const DUPLICATE_SKIP = "İlgilendiği hizmet";

/* Tüm 12 sektör artık tek ortak motoru kullanır. Sektör kimliği ayrıca
   DemoFlow.sector içinde korunur. */
export function selectTemplate(_sektor: string): string {
  return "standard_sector_flow_v1";
}

function channelDestination(form: DemoFormData): string {
  if (form.anaIletisimKanali === "instagram") return form.instagram;
  if (form.anaIletisimKanali === "telefon") return form.whatsapp;
  return form.whatsapp;
}

function channelCtaLabel(form: DemoFormData): string {
  if (form.anaIletisimKanali === "instagram") return "Instagram Profiline Git";
  if (form.anaIletisimKanali === "telefon") return "Telefonla Ara";
  return "WhatsApp'tan İletişime Geç";
}

/* Telefon numarasını uluslararası formata normalize eder (wa.me / tel: için). */
export function normalizePhone(raw: string): string {
  let d = raw.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = d.slice(1);
  if (d.length === 10) d = `90${d}`;
  return d;
}

/* Ana iletişim kanalına göre gerçek yönlendirme linki üretir. */
export function buildCtaUrl(channel: IletisimKanali, form: DemoFormData): string | null {
  if (channel === "whatsapp") {
    const p = normalizePhone(form.whatsapp);
    return p ? `https://wa.me/${p}` : null;
  }
  if (channel === "telefon") {
    const p = normalizePhone(form.whatsapp);
    return p ? `tel:+${p}` : null;
  }
  const handle = form.instagram.trim();
  if (!handle) return null;
  if (/^https?:\/\//i.test(handle)) return handle;
  const username = handle.replace(/^@/, "");
  return username ? `https://instagram.com/${username}` : null;
}

/* Aksiyon destination'ına göre hedef adresi çözer (display amaçlı, crash YOK).
   phone için ayrı telefon alanı yok; canonical numara form.whatsapp kullanılır. */
export function resolveActionTarget(
  action: DemoFlowActionSetAction,
  form: DemoFormData,
): string | null {
  switch (action.destination) {
    case "whatsapp":
      return form.whatsapp.trim() || null;
    case "website":
      return form.website.trim() || null;
    case "instagram":
      return form.instagram.trim() || null;
    case "phone":
      return form.whatsapp.trim() || null;
    default:
      return null;
  }
}

/* Completion SONRASI harici test linki üretir. Completion öncesi hiçbir yerde
   kullanılmaz; external link yalnızca completion sonrası açılır. */
export function buildExternalTestUrl(
  action: DemoFlowActionSetAction,
  form: DemoFormData,
): string | null {
  if (action.destination === "website") {
    return form.website.trim() || null;
  }
  if (action.destination === "whatsapp") {
    const base = buildCtaUrl("whatsapp", form);
    if (!base) return null;
    return action.prefill ? `${base}?text=${encodeURIComponent(action.prefill)}` : base;
  }
  if (action.destination === "instagram") {
    return buildCtaUrl("instagram", form);
  }
  if (action.destination === "phone") {
    return buildCtaUrl("telefon", form);
  }
  return null;
}

/* Güçlü yön etiketlerini akıcı cümle parçalarına çevirir. */
const STRENGTH_PHRASE: Record<string, string> = {
  "Uzman Personel": "uzman ekibimiz",
  "Tecrübe": "tecrübemiz",
  "Uygun Fiyat": "uygun fiyatlarımız",
  "Premium Hizmet": "premium hizmet anlayışımız",
  "Kaliteli / Gelişmiş Ekipman": "kaliteli ekipmanlarımız",
  "Hijyen & Güven": "hijyen ve güven odaklı çalışmamız",
  "Merkezi / Kolay Konum": "merkezi konumumuz",
  "Yüksek Müşteri Memnuniyeti": "yüksek müşteri memnuniyetimiz",
  "Hızlı Hizmet": "hızlı hizmetimiz",
};

function joinTurkish(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  return items.slice(0, -1).join(", ") + " ve " + items[items.length - 1];
}

/* beauty_v2 için markalı, veriye göre esnek karşılama metni üretir.
   Tecrübe / güçlü yönler yoksa ilgili cümle düzgün şekilde atlanır.
   "%100 memnuniyet" benzeri garantiler yalnızca kullanıcı belirtmişse
   (guvenNotu / gucluYonler) metne girer; kendiliğinden üretilmez. */
function buildBeautyV2Welcome(form: DemoFormData): DemoFlowStep {
  const name = form.firmaAdi.trim();
  const opening = name ? `${name}'na hoş geldiniz 👋` : "Hoş geldiniz 👋";

  const parts: string[] = [opening];

  const segments: string[] = [];
  const years = form.tecrubeYili;
  if (years && years > 0) segments.push(`${years} yıllık deneyimimiz`);
  for (const s of form.gucluYonler) {
    const phrase = STRENGTH_PHRASE[s] ?? s.trim();
    if (phrase) segments.push(phrase);
  }

  if (segments.length > 0) {
    parts.push(`${joinTurkish(segments)} ile güzellik ve bakım alanında hizmet vermekteyiz.`);
  }

  parts.push(
    "Bu destek hattımız üzerinden çalışmalarımızı, işlem örneklerimizi ve güncel kampanyalarımızı inceleyebilirsiniz.",
  );
  parts.push(
    "Tüm incelemenizi tamamladıktan sonra randevu oluşturabilir veya sorularınız için ekibimizle doğrudan iletişime geçebilirsiniz.",
  );

  const note = form.guvenNotu.trim();
  if (note) parts.push(note);

  return { id: "welcome", type: "welcome", message: parts.join("\n\n") };
}

function welcomeStep(form: DemoFormData): DemoFlowStep {
  const name = form.firmaAdi.trim() || "bize";
  return { id: "welcome", type: "welcome", message: `Merhaba 👋 ${name}'e hoş geldiniz.` };
}

function serviceChoiceStep(id: string, message: string, form: DemoFormData): DemoFlowStep {
  return { id, type: "choice", message, options: form.hizmetler };
}

function campaignStep(form: DemoFormData): DemoFlowStep | null {
  const text = form.kampanyaMetni.trim();
  if (!text) return null;
  return {
    id: "campaign",
    type: "text",
    message: `Size özel güncel kampanyamızı da paylaşalım ✨\n\n${text}`,
  };
}

function firstMediaStep(form: DemoFormData): DemoFlowStep | null {
  if (form.gorseller.length > 0) return { id: "media-0", type: "image", mediaIndex: 0 };
  if (form.videolar.length > 0) return { id: "media-v0", type: "video", mediaIndex: 0 };
  return null;
}

/* beauty_v2 canonical deterministic akış:
   branded welcome → gallery intro → her kategori için gallery →
   video intro + videolar (varsa) → campaign intro + kampanya listesi (varsa) →
   contact intro → dual CTA (Randevu / Soru Sor).
   Qualification question'ları bu akışa girmez. */
function buildBeautyV2Flow(form: DemoFormData): DemoFlowStep[] {
  const steps: DemoFlowStep[] = [];

  // 1. Markalı karşılama
  steps.push(buildBeautyV2Welcome(form));

  // 2. Hizmet kategorisi galerileri
  const kategoriler = form.serviceCategories.filter((k) => k.images.length > 0);
  if (kategoriler.length > 0) {
    steps.push({
      id: "gallery-intro",
      type: "text",
      message: "Öncelikle çalışmalarımızdan bazı örnekleri sizinle paylaşmak istiyoruz ✨",
    });
    kategoriler.forEach((k, i) => {
      steps.push({
        id: `gallery-${i}`,
        type: "gallery",
        title: k.name,
        categoryName: k.name,
        mediaUrls: k.images,
      });
    });
  }

  // 3. İşlem / sonuç videoları (opsiyonel)
  const videos = form.videolar.slice(0, 2);
  if (videos.length > 0) {
    steps.push({
      id: "video-intro",
      type: "text",
      message:
        "Şimdi uygulamalarımızı daha yakından görebileceğiniz kısa videolarımızı inceleyebilirsiniz 🎥",
    });
    videos.forEach((_, i) => {
      steps.push({ id: `video-${i}`, type: "video", mediaIndex: i });
    });
  }

  // 4. Güncel kampanyalar (opsiyonel)
  const kampanyalar = form.campaigns;
  if (kampanyalar.length > 0) {
    steps.push({
      id: "campaign-intro",
      type: "text",
      message: "✨ Şu an güncel kampanyalarımız",
    });
    steps.push({
      id: "campaign-list",
      type: "campaign_list",
      campaigns: kampanyalar,
    });
  }

  // 5. Final iletişim yönlendirmesi
  steps.push({
    id: "contact-intro",
    type: "text",
    message:
      "Dilerseniz hemen bizimle iletişime geçebilir, randevu oluşturabilir veya sorularınız için ekibimize ulaşabilirsiniz. 💬",
  });

  const waUrl = buildCtaUrl("whatsapp", form) ?? form.whatsapp;
  steps.push({
    id: "dual-cta",
    type: "dual_cta",
    actions: [
      {
        label: "📅 Hemen Randevu Oluştur",
        prefill:
          "Merhaba, kampanyalarınızı ve çalışmalarınızı inceledim. Randevu oluşturmak istiyorum.",
        destination: waUrl,
        channel: "whatsapp",
      },
      {
        label: "💬 Soru Sormak İstiyorum",
        prefill: "Merhaba, işlemleriniz hakkında birkaç soru sormak istiyorum.",
        destination: waUrl,
        channel: "whatsapp",
      },
    ],
  });

  return steps;
}

function buildQuestions(
  form: DemoFormData,
  opts?: { skipService?: boolean; extraDefaults?: string[] },
): DemoFlowQuestionStep[] {
  const steps: DemoFlowQuestionStep[] = [];
  let idx = 0;
  for (const preset of form.sorular) {
    if (opts?.skipService && preset === DUPLICATE_SKIP) continue;
    const sentence = SORU_SENTENCE_MAP[preset];
    if (!sentence) continue;
    steps.push({ id: `question-${idx++}`, type: "question", message: sentence });
  }
  for (const custom of form.ozelSorular) {
    const c = custom.trim();
    if (!c) continue;
    steps.push({ id: `question-${idx++}`, type: "question", message: c });
  }
  for (const extra of opts?.extraDefaults ?? []) {
    steps.push({ id: `question-${idx++}`, type: "question", message: extra });
  }
  return steps;
}

function buildCtaStep(form: DemoFormData, message: string): DemoFlowCtaStep {
  return {
    id: "cta",
    type: "cta",
    message,
    label: channelCtaLabel(form),
    destination: channelDestination(form),
    channel: form.anaIletisimKanali,
  };
}

function buildCleaningFlow(form: DemoFormData): DemoFlowStep[] {
  const steps: DemoFlowStep[] = [];
  steps.push(welcomeStep(form));
  if (form.hizmetler.length > 0) {
    steps.push(serviceChoiceStep("service-choice", "Hangi temizlik hizmetiyle ilgileniyorsunuz?", form));
  }
  const media = firstMediaStep(form);
  if (media) steps.push(media);
  const extraDefaults =
    form.sorular.length <= 3
      ? ["Hizmet nerede alınacak?", "Yaklaşık alan / oda bilgisi nedir?"]
      : [];
  steps.push(...buildQuestions(form, { skipService: true, extraDefaults }));
  const campaign = campaignStep(form);
  if (campaign) steps.push(campaign);
  steps.push(buildCtaStep(form, "Detaylı bilgi ve fiyat için ekibimizle iletişime geçin."));
  return steps;
}

function buildEventFlow(form: DemoFormData): DemoFlowStep[] {
  const steps: DemoFlowStep[] = [];
  steps.push(welcomeStep(form));
  if (form.hizmetler.length > 0) {
    steps.push(serviceChoiceStep("event-choice", "Hangi organizasyon türüyle ilgileniyorsunuz?", form));
  }
  const media = firstMediaStep(form);
  if (media) steps.push(media);
  steps.push(...buildQuestions(form, { skipService: true }));
  const campaign = campaignStep(form);
  if (campaign) steps.push(campaign);
  steps.push(buildCtaStep(form, "Uygunluk ve teklif için iletişime geçin."));
  return steps;
}

function buildGenericFlow(form: DemoFormData): DemoFlowStep[] {
  const steps: DemoFlowStep[] = [];
  steps.push(welcomeStep(form));
  if (form.hizmetler.length > 0) {
    steps.push(serviceChoiceStep("service-choice", "Hangi hizmetimizle ilgileniyorsunuz?", form));
  }
  const media = firstMediaStep(form);
  if (media) steps.push(media);
  const campaign = campaignStep(form);
  if (campaign) steps.push(campaign);
  steps.push(...buildQuestions(form, { skipService: true }));
  steps.push(buildCtaStep(form, "Detaylı bilgi için ekibimizle iletişime geçin."));
  return steps;
}

/* Ortak markalı karşılama — firma adı, tecrübe, güçlü yönler, hizmet bölgesi
   ve güven notundan deterministic metin üretir. Sektör noun'u config label'ından gelir. */
function buildStandardWelcome(form: DemoFormData, sectorConfig?: SectorConfig): DemoFlowStep {
  const name = form.firmaAdi.trim();
  const opening = name ? `${name}'na hoş geldiniz 👋` : "Hoş geldiniz 👋";
  const parts: string[] = [opening];

  const segments: string[] = [];
  const years = form.tecrubeYili;
  if (years && years > 0) segments.push(`${years} yıllık deneyimimiz`);
  for (const s of form.gucluYonler) {
    const phrase = STRENGTH_PHRASE[s] ?? s.trim();
    if (phrase) segments.push(phrase);
  }

  const sectorLabel = sectorConfig?.label ?? "hizmet";

  if (segments.length > 0) {
    parts.push(`${joinTurkish(segments)} ile ${sectorLabel} alanında hizmet vermekteyiz.`);
  } else {
    parts.push(`${sectorLabel} alanında sizlere hizmet vermekten mutluluk duyuyoruz.`);
  }

  if (form.hizmetBolgesi.trim()) {
    parts.push(`Hizmet bölgemiz: ${form.hizmetBolgesi}.`);
  }

  parts.push(
    "Bu destek hattımız üzerinden hizmetlerimizi, çalışmalarımızı ve varsa güncel kampanyalarımızı inceleyebilirsiniz. İncelemenizin sonunda ekibimizle doğrudan iletişime geçebilirsiniz.",
  );

  const note = form.guvenNotu.trim();
  if (note) parts.push(note);

  return { id: "welcome", type: "welcome", message: parts.join("\n\n") };
}

/* Sektöre göre ilgi seçim sorusu (hizmet / ürün / program / portföy vb.). */
const CHOICE_MESSAGE: Record<string, string> = {
  beauty: "Hangi hizmetimizle ilgileniyorsunuz?",
  cleaning: "Hangi hizmetimizle ilgileniyorsunuz?",
  event: "Hangi organizasyon türüyle ilgileniyorsunuz?",
  education: "Hangi programımızla ilgileniyorsunuz?",
  realestate: "Hangi portföy kategorisiyle ilgileniyorsunuz?",
  food: "Hangi menü kategorisiyle ilgileniyorsunuz?",
  furniture: "Hangi ürün kategorisiyle ilgileniyorsunuz?",
  network: "Hangi ürün kategorisiyle ilgileniyorsunuz?",
  onlinesales: "Hangi ürün kategorisiyle ilgileniyorsunuz?",
  localproducts: "Hangi ürün kategorisiyle ilgileniyorsunuz?",
  professional: "Hangi hizmetimizle ilgileniyorsunuz?",
  other: "Hangi hizmetimizle ilgileniyorsunuz?",
};

function serviceChoiceMessage(config?: SectorConfig): string {
  if (!config) return "Hangi hizmetimizle ilgileniyorsunuz?";
  return CHOICE_MESSAGE[config.id] ?? "Hangi hizmetimizle ilgileniyorsunuz?";
}

/* standard_sector_flow_v1 — 12 sektörün ortak deterministic akışı:
   welcome → gallery intro + kategoriler → video intro + videolar →
   hizmet/kategori seçimi → kampanyalar → contact intro → action_set. */
function buildStandardSectorFlow(form: DemoFormData): DemoFlowStep[] {
  const steps: DemoFlowStep[] = [];
  const sectorConfig = findSectorConfig(form.sektor);

  // 1. Markalı karşılama
  steps.push(buildStandardWelcome(form, sectorConfig));

  // 2. Galeri intro + her kategori için galeri
  const kategoriler = form.serviceCategories.filter((k) => k.images.length > 0);
  if (kategoriler.length > 0) {
    const itemLabel = sectorConfig ? getItemLabel(sectorConfig) : "hizmet";
    const galleryIntro = itemLabel.includes("ürün")
      ? "Ürünlerimizden bazı örnekleri sizinle paylaşmak istiyoruz ✨"
      : "Öncelikle çalışmalarımızdan bazı örnekleri sizinle paylaşmak istiyoruz ✨";
    steps.push({ id: "gallery-intro", type: "text", message: galleryIntro });
    kategoriler.forEach((k, i) => {
      steps.push({
        id: `gallery-${i}`,
        type: "gallery",
        title: k.name,
        categoryName: k.name,
        mediaUrls: k.images,
      });
    });
  }

  // 3. Videolar (opsiyonel)
  const videos = form.videolar.slice(0, 2);
  if (videos.length > 0) {
    steps.push({
      id: "video-intro",
      type: "text",
      message:
        "Şimdi işletmemizi, ürünlerimizi veya uygulamalarımızı daha yakından görebileceğiniz kısa videoları inceleyebilirsiniz 🎥",
    });
    videos.forEach((_, i) => {
      steps.push({ id: `video-${i}`, type: "video", mediaIndex: i });
    });
  }

  // 4. Hizmet / kategori ilgisi seçimi
  const choiceCategories = form.serviceCategories.filter((k) => k.name.trim());
  if (choiceCategories.length > 0) {
    steps.push({
      id: "service-choice",
      type: "choice",
      message: serviceChoiceMessage(sectorConfig),
      options: choiceCategories.map((c) => c.name),
    });
  }

  // 5. Kampanyalar (opsiyonel)
  const kampanyalar = form.campaigns;
  if (kampanyalar.length > 0) {
    steps.push({ id: "campaign-intro", type: "text", message: "✨ Şu an güncel kampanyalarımız" });
    steps.push({ id: "campaign-list", type: "campaign_list", campaigns: kampanyalar });
  }

  // 6. İletişim intro
  steps.push({
    id: "contact-intro",
    type: "text",
    message:
      "Dilerseniz hemen bizimle iletişime geçebilir, bilgi veya teklif alabilir ya da diğer iletişim seçeneklerimizden size uygun olanı kullanabilirsiniz. 💬",
  });

  // 7. CTA aksiyon seti — selectedCtas üzerinden (boşsa sektör defaults)
  const sourceCtas =
    form.selectedCtas.length > 0 ? form.selectedCtas : (sectorConfig?.defaultCtas ?? []);
  steps.push({
    id: "action-set",
    type: "action_set",
    actions: sourceCtas.map((cta) => ({
      id: cta.id,
      label: cta.label,
      destination: cta.destination,
      prefill: cta.prefill,
      intent: resolveCtaIntent(cta),
    })),
  });

  return steps;
}

export function buildDemoFlow(form: DemoFormData): DemoFlow {
  const templateId = selectTemplate(form.sektor);
  const steps = buildStandardSectorFlow(form);
  return {
    id: `flow-${templateId}`,
    templateId,
    sector: form.sektor,
    businessName: form.firmaAdi,
    intro: `${form.firmaAdi || "İşletmeniz"} için hazırlanan müşteri iletişim akışı`,
    steps,
    destination: channelDestination(form),
  };
}

/* ---------- Özet / görselleştirme yardımcıları ---------- */

function ctaLabel(step: DemoFlowCtaStep): string {
  if (step.channel === "instagram") return "Instagram yönlendirmesi";
  if (step.channel === "telefon") return "Telefon yönlendirmesi";
  return "WhatsApp yönlendirmesi";
}

function stepSummaryLabel(step: DemoFlowStep): string {
  switch (step.type) {
    case "welcome":
      return "Karşılama";
    case "text":
      return "Kampanya";
    case "choice":
      return "Hizmet seçimi";
    case "gallery":
      return "Hizmet galerisi";
    case "campaign_list":
      return "Kampanya";
    case "image":
      return "Görsel";
    case "video":
      return "Video";
    case "question":
      return "Müşteri sorusu";
    case "cta":
      return ctaLabel(step);
    case "dual_cta":
      return "Randevu / Soru Sor yönlendirmesi";
    case "action_set":
      return "İletişim seçeneği";
    default:
      return "Adım";
  }
}

export interface FlowSummary {
  title: string;
  bullets: string[];
}

export function summarizeFlow(flow: DemoFlow): FlowSummary {
  const bullets: string[] = [];
  let galleryCount = 0;
  let i = 0;
  while (i < flow.steps.length) {
    const step = flow.steps[i];
    if (step.type === "gallery") {
      galleryCount += 1;
      i += 1;
      continue;
    }
    if (step.type === "campaign_list") {
      const n = step.campaigns.length;
      bullets.push(n === 1 ? "1 güncel kampanya" : `${n} güncel kampanya`);
      i += 1;
      continue;
    }
    if (step.type === "action_set") {
      const n = step.actions.length;
      bullets.push(n === 1 ? "1 iletişim seçeneği" : `${n} iletişim seçeneği`);
      i += 1;
      continue;
    }
    if (step.type === "question") {
      let count = 0;
      while (i < flow.steps.length && flow.steps[i].type === "question") {
        count += 1;
        i += 1;
      }
      bullets.push(count === 1 ? "Müşteri sorusu" : `${count} müşteri sorusu`);
      continue;
    }
    bullets.push(stepSummaryLabel(step));
    i += 1;
  }
  if (galleryCount > 0) {
    bullets.unshift(galleryCount === 1 ? "1 hizmet galerisi" : `${galleryCount} hizmet galerisi`);
  }
  const title = `${flow.businessName || "İşletmeniz"} için demo hazır`;
  return { title, bullets };
}

export interface FlowStepDetail {
  label: string;
  content: string;
}

function collapse(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

export function describeFlowSteps(flow: DemoFlow): FlowStepDetail[] {
  return flow.steps.map((step) => {
    switch (step.type) {
      case "welcome":
        return { label: stepSummaryLabel(step), content: collapse(step.message) };
      case "text":
        return { label: stepSummaryLabel(step), content: collapse(step.message) };
      case "question":
        return { label: stepSummaryLabel(step), content: step.message };
      case "choice":
        return { label: stepSummaryLabel(step), content: step.options.join(" / ") };
      case "gallery":
        return { label: "Hizmet galerisi", content: step.categoryName };
      case "campaign_list":
        return { label: "Kampanya", content: step.campaigns.map((c) => c.name).join(" / ") };
      case "image":
        return { label: "Görsel", content: `Görsel ${step.mediaIndex + 1}` };
      case "video":
        return { label: "Video", content: `Video ${step.mediaIndex + 1}` };
      case "cta":
        return { label: ctaLabel(step), content: step.label };
      case "dual_cta":
        return { label: "Yönlendirme", content: step.actions.map((a) => a.label).join(" / ") };
      case "action_set":
        return { label: "İletişim seçenekleri", content: step.actions.map((a) => a.label).join(" / ") };
      default:
        return { label: "Adım", content: "" };
    }
  });
}